import { defineStore } from 'pinia';
import { getAccountSettings, updateAccountSettings } from '@/api/settings';
import { handleKnownSettingsApiErrors } from '@/stores/settings/errorHandling';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppLoading } from '@/stores/app/loading';
import { useSettingsAccountForm } from '@/stores/settings/accountForm';

export const useSettingsAccount = defineStore('settingsAccount', () => {
  // other stores
  const form = useSettingsAccountForm();
  const { setLoading } = useAppLoading();
  const { addErrorSnackbar, addSuccessSnackbar } = useAppSnackbar();

  // check if the error is validation error and update form accordingly
  const handleValidationError = (error) => {
    const { response } = error;

    if (response && response.code === 'validation_failed') {
      form.setFormValues(response.attributes);
      form.setBackendErrors(response.errors);
      return true;
    }

    return false;
  };

  // check if the error is known or generic and show proper alert
  const handleError = (error) => {
    const isKnownError = handleKnownSettingsApiErrors({ context: 'Account settings', error });

    if (!isKnownError) {
      addErrorSnackbar({ text: `Account settings - ${error.message}` });
      throw error;
    }
  };

  const load = async () => {
    try {
      setLoading({ pageLoading: true });
      form.setFormValues(await getAccountSettings());
    } catch (error) {
      handleError(error);
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const update = async () => {
    if (!form.isFormValid) {
      return;
    }

    try {
      setLoading({ componentLoading: true });

      const { firstName, lastName } = form.values;
      const data = await updateAccountSettings({ firstName, lastName });

      if (data.success) {
        form.setFormValues(data.attributes);
        addSuccessSnackbar({
          text: 'Your account has been updated',
        });
      }
    } catch (error) {
      if (!handleValidationError(error)) {
        handleError(error);
      }
    } finally {
      setLoading({ componentLoading: false });
    }
  };

  return { load, update };
});
